"use client";

import { styled } from "@mui/material/styles";
import Button, { ButtonProps } from "@mui/material/Button";
import Box from "@mui/material/Box";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons/faArrowRight";
import { faUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons/faUpRightFromSquare";

export type FunnelButtonIcon = "arrow" | "external" | "none";
export type FunnelButtonAppearance = "filled" | "outlined" | "cta";

const TRAILING_ICONS = {
  arrow: faArrowRight,
  external: faUpRightFromSquare,
} as const;

const BUTTON_VARIANTS = {
  filled: "contained",
  outlined: "outlined",
  cta: "cta",
} as const;

interface FunnelButtonProps extends Omit<ButtonProps, "variant" | "color"> {
  /**
   * Trailing icon. Defaults to the navigation arrow so in-funnel steps are
   * unchanged. Use `external` for a CTA that hands the user off to a provider
   * site, so the button reads as a link-out rather than another funnel step.
   */
  icon?: FunnelButtonIcon;
  /**
   * `outlined` is the white/primary-border treatment. It exists so a screen can
   * show several CTAs at once without them competing: the primary choice stays
   * filled and the alternatives recede. `cta` is the green program-card call to
   * action (the theme's `cta` button variant).
   */
  appearance?: FunnelButtonAppearance;
}

/**
 * Primary button for form funnel navigation. Filled navy by default, `cta` for
 * the green program-card call to action, `outlined` for screens that show
 * competing CTAs at once, and a trailing icon that distinguishes moving through
 * the funnel from leaving it.
 */
export const FunnelButton = ({
  children,
  icon = "arrow",
  appearance = "filled",
  ...props
}: FunnelButtonProps) => (
  // `appearance` maps onto MUI's own variant rather than restyling a contained
  // button, so the outlined treatment inherits the theme's border/hover/disabled
  // handling instead of competing with `contained`'s rules at equal specificity.
  <StyledButton
    $appearance={appearance}
    variant={BUTTON_VARIANTS[appearance]}
    color="primary"
    {...props}
  >
    <ButtonContent>
      <span>{children}</span>
      {icon !== "none" && (
        <FontAwesomeIcon icon={TRAILING_ICONS[icon]} style={{ fontSize: 14, marginLeft: 6 }} />
      )}
    </ButtonContent>
  </StyledButton>
);

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "$appearance",
})<{ $appearance: FunnelButtonAppearance }>(({ theme, $appearance }) => ({
  minHeight: 48,
  width: "100%",
  maxWidth: 320,
  display: "flex",
  boxShadow: "none",

  // Border, text color and the disabled treatment come from the theme's
  // `outlined`/`outlinedPrimary` overrides; only the opaque fill and the funnel's
  // own hover/press feedback are specified here.
  ...($appearance === "outlined"
    ? {
        backgroundColor: theme.palette.common.white,

        "&:hover": {
          backgroundColor: theme.palette.dream.grayLighter,
          borderColor: theme.palette.primary.dark,
          boxShadow: "none",
        },

        "&:active": {
          backgroundColor: theme.palette.dream.grayLight,
          transform: "translateY(1px)",
        },
      }
    : {
        "&:hover": {
          backgroundColor:
            $appearance === "cta"
              ? theme.palette.dream.ctaBackgroundHover
              : theme.palette.primary.dark,
          boxShadow: "0 2px 8px rgba(34, 34, 85, 0.3)",
        },

        "&:active": {
          backgroundColor:
            $appearance === "cta"
              ? theme.palette.dream.ctaBackgroundHover
              : theme.palette.primary.dark,
          transform: "translateY(1px)",
        },

        "&:disabled": {
          backgroundColor: "#b0b0b0",
          color: "white",
          opacity: 0.7,
        },
      }),

  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

const ButtonContent = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

/**
 * Submit button wrapper for Formik forms.
 * Centered with proper spacing for form funnel layouts.
 */
export const FunnelSubmitWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  flex-grow: 1;
  margin-top: 1.5rem;
  padding-top: 1rem;
`;
